// 📄 context/AuthContext.tsx
import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../firebase';
import { ensureUserProfile, subscribeUserProfile, UserProfile } from '../services/userService.ts';
import { esClienteEnAlgunProyecto } from '../services/projectService.ts';
import { getUserOrganizations } from '../services/organizationService.ts';
import { Organization } from '../types/organization';

// Org activa recordada POR USUARIO (2026-10-26): antes era una sola clave
// que se borraba al cerrar sesión, así que cada ingreso volvía a la org
// personal. Con la clave por uid no se mezcla entre usuarios del mismo
// navegador y la elección sobrevive al logout.
const LEGACY_ACTIVE_ORG_STORAGE_KEY = 'onehub:orgActivaId';
const activeOrgKey = (uid: string) => `onehub:orgActivaId:${uid}`;
// Orgs que el usuario ya conoce, para avisarle cuando lo agregan a una nueva.
const knownOrgsKey = (uid: string) => `onehub:orgsConocidas:${uid}`;

const leer = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const escribir = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage no disponible (modo privado, etc.) — la app sigue
    // funcionando, solo no persiste la elección entre sesiones.
  }
};

const borrar = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // idem arriba
  }
};

const millis = (ts: any): number => ts?.toMillis?.() ?? 0;

/**
 * Org por defecto cuando el usuario todavía no eligió una en este navegador
 * (ej. su primer ingreso después de que lo invitaron): el equipo al que se
 * unió más recientemente; si no tiene equipos, su org personal.
 */
const orgPorDefecto = (orgs: Organization[]): string | null => {
  const equipo = orgs
    .filter(o => o.type === 'equipo' && !o.archivada)
    .sort((a, b) => millis(b.unidoEn) - millis(a.unidoEn))[0];
  if (equipo) return equipo.id;
  return orgs.find(o => o.type === 'personal')?.id || orgs[0]?.id || null;
};

interface AuthContextValue {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  // "Cuenta externa": tiene rol "cliente" en al menos un proyecto. Se
  // recalcula en vivo al cambiar de usuario (ver esClienteEnAlgunProyecto).
  esClienteExterno: boolean;
  // Sub-fase G1: organizaciones del usuario + cuál está activa. Vive acá
  // (no en un hook separado) para que haya un solo fetch — useOrganizations()
  // es ahora un wrapper delgado sobre este mismo estado (ver
  // src/hooks/useOrganizations.ts), evitando dos fetches paralelos
  // desincronizados sobre el mismo localStorage key.
  organizations: Organization[];
  orgActual: Organization | null;
  orgsLoading: boolean;
  setOrgActual: (orgId: string) => void;
  reloadOrganizations: () => void;
  // Equipos a los que lo agregaron desde la última vez que entró (no los
  // que creó él). Inicio muestra un aviso para ir a ellos.
  orgsNuevas: Organization[];
  marcarOrgsConocidas: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  profile: null,
  loading: true,
  esClienteExterno: false,
  organizations: [],
  orgActual: null,
  orgsLoading: true,
  setOrgActual: () => {},
  reloadOrganizations: () => {},
  orgsNuevas: [],
  marcarOrgsConocidas: () => {}
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [esClienteExterno, setEsClienteExterno] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgActualId, setOrgActualIdState] = useState<string | null>(null);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsNuevas, setOrgsNuevas] = useState<Organization[]>([]);
  const unsubscribeProfileRef = useRef<(() => void) | null>(null);

  const loadOrganizations = useCallback(async (uid: string) => {
    setOrgsLoading(true);
    try {
      const orgs = await getUserOrganizations(uid);
      setOrganizations(orgs);
      // Valida que la org guardada siga existiendo y el usuario siga
      // perteneciendo a ella (la lista recién cargada es la fuente de
      // verdad). Si no, la org por defecto (ver orgPorDefecto).
      // (La clave vieja, compartida, solo como respaldo para sesiones abiertas antes del cambio.)
      const guardada = leer(activeOrgKey(uid)) || leer(LEGACY_ACTIVE_ORG_STORAGE_KEY);
      const elegida = guardada && orgs.some(o => o.id === guardada) ? guardada : orgPorDefecto(orgs);
      setOrgActualIdState(elegida);

      // Orgs nuevas: la primera vez en este navegador todo cuenta como
      // conocido (y ya entra al equipo por defecto); después, se avisa de
      // los equipos que aparecieron y que no creó él.
      const conocidasRaw = leer(knownOrgsKey(uid));
      if (conocidasRaw === null) {
        escribir(knownOrgsKey(uid), JSON.stringify(orgs.map(o => o.id)));
        setOrgsNuevas([]);
      } else {
        let conocidas: string[] = [];
        try { conocidas = JSON.parse(conocidasRaw); } catch { /* clave corrupta: se trata como vacía */ }
        setOrgsNuevas(orgs.filter(o =>
          o.type === 'equipo' && o.role !== 'owner' && !conocidas.includes(o.id) && o.id !== elegida
        ));
      }
    } catch (err) {
      console.error('Error cargando organizaciones del usuario:', err);
      setOrganizations([]);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      // Cierra el listener del perfil anterior antes de abrir uno nuevo
      if (unsubscribeProfileRef.current) {
        unsubscribeProfileRef.current();
        unsubscribeProfileRef.current = null;
      }

      setUser(currentUser);

      if (!currentUser) {
        setProfile(null);
        setEsClienteExterno(false);
        setLoading(false);
        // Invalidación explícita al cerrar sesión: nada de la org de la
        // sesión anterior debe sobrevivir a un logout (ni en memoria ni
        // en localStorage) — el próximo login (mismo usuario u otro)
        // arranca limpio y recalcula desde cero.
        setOrganizations([]);
        setOrgActualIdState(null);
        setOrgsNuevas([]);
        setOrgsLoading(false);
        borrar(LEGACY_ACTIVE_ORG_STORAGE_KEY);
        return;
      }

      await ensureUserProfile(currentUser.uid, currentUser.email, currentUser.displayName);
      unsubscribeProfileRef.current = subscribeUserProfile(currentUser.uid, (p) => {
        setProfile(p);
        setLoading(false);
      });

      esClienteEnAlgunProyecto(currentUser.uid)
        .then(setEsClienteExterno)
        .catch((err) => {
          console.error('Error calculando cuenta externa:', err);
          setEsClienteExterno(false);
        });

      loadOrganizations(currentUser.uid);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfileRef.current) unsubscribeProfileRef.current();
    };
  }, [loadOrganizations]);

  const marcarOrgsConocidas = useCallback(() => {
    if (!user) return;
    escribir(knownOrgsKey(user.uid), JSON.stringify(organizations.map(o => o.id)));
    setOrgsNuevas([]);
  }, [user, organizations]);

  const setOrgActual = useCallback((orgId: string) => {
    setOrgActualIdState(orgId);
    if (user) {
      escribir(activeOrgKey(user.uid), orgId);
      // Ir a una org nueva cuenta como "ya la vio".
      let conocidas: string[] = [];
      try { conocidas = JSON.parse(leer(knownOrgsKey(user.uid)) || '[]'); } catch { /* idem loadOrganizations */ }
      if (!conocidas.includes(orgId)) escribir(knownOrgsKey(user.uid), JSON.stringify([...conocidas, orgId]));
    }
    setOrgsNuevas(prev => prev.filter(o => o.id !== orgId));
  }, [user]);

  const reloadOrganizations = useCallback(() => {
    if (user) loadOrganizations(user.uid);
  }, [user, loadOrganizations]);

  const orgActual = organizations.find(o => o.id === orgActualId) || null;

  return (
    <AuthContext.Provider value={{
      user, profile, loading, esClienteExterno,
      organizations, orgActual, orgsLoading, setOrgActual, reloadOrganizations,
      orgsNuevas, marcarOrgsConocidas
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
